import { forwardRef, useId } from 'react'
import type { SelectHTMLAttributes, ReactNode } from 'react'
import { fieldControl } from './formClasses'
import { IconChevronDown } from './Icons'

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  className?: string
  children?: ReactNode
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, className = '', children, id: providedId, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, ...props },
  ref
) {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [ariaDescribedBy, errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-ink-secondary mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          aria-invalid={error ? true : ariaInvalid}
          aria-describedby={describedBy}
          className={`${fieldControl(error)} appearance-none pr-10 cursor-pointer ${className}`}
          {...props}
        >
          {children}
        </select>
        <IconChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted" />
      </div>
      {hint && !error && <p id={hintId} className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {error && <p id={errorId} role="alert" className="mt-1 text-sm text-err-ink">{error}</p>}
    </div>
  )
})
